import React from 'react'
import { X, Clock, MapPin, Users, Calendar, ExternalLink, FileText } from 'lucide-react'
import { format, parseISO } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import type { CalendarEvent } from '@/hooks/useCalendarEvents'

interface EventDetailModalProps {
  event: CalendarEvent | null
  isOpen: boolean
  onClose: () => void
}

const EventDetailModal: React.FC<EventDetailModalProps> = ({
  event,
  isOpen,
  onClose
}) => {
  if (!isOpen || !event) return null

  const formatDateTime = (dateString: string) => {
    try {
      const date = parseISO(dateString)
      return format(date, "EEEE, d 'de' MMMM 'de' yyyy 'as' HH:mm", { locale: ptBR })
    } catch {
      return dateString
    }
  }

  const formatTime = (dateString: string) => {
    try {
      const date = parseISO(dateString)
      return format(date, 'HH:mm', { locale: ptBR })
    } catch {
      return ''
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      <div className="relative bg-neutral-900 border border-neutral-700 rounded-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b border-neutral-800">
          <h2 className="text-lg font-semibold text-neutral-50">
            Detalhes do Evento
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-neutral-800 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-neutral-400" />
          </button>
        </div>

        <div className="p-4 overflow-y-auto max-h-[calc(90vh-140px)]">
          <h3 className="text-xl font-semibold text-neutral-50 mb-4">
            {event.title || 'Sem titulo'}
          </h3>

          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <Calendar className="w-5 h-5 text-emerald-500 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-neutral-300">Data e Hora</p>
                <p className="text-sm text-neutral-400 capitalize">
                  {formatDateTime(event.start)}
                </p>
                {event.end && (
                  <p className="text-sm text-neutral-500">
                    Termina as {formatTime(event.end)}
                  </p>
                )}
              </div>
            </div>

            {event.location && (
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-emerald-500 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-neutral-300">Local</p>
                  <p className="text-sm text-neutral-400">{event.location}</p>
                </div>
              </div>
            )}

            {event.description && (
              <div className="flex items-start gap-3">
                <FileText className="w-5 h-5 text-emerald-500 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-neutral-300">Descricao</p>
                  <p className="text-sm text-neutral-400 whitespace-pre-wrap">
                    {event.description}
                  </p>
                </div>
              </div>
            )}

            {event.attendees && event.attendees.length > 0 && (
              <div className="flex items-start gap-3">
                <Users className="w-5 h-5 text-emerald-500 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-neutral-300">
                    Participantes ({event.attendees.length})
                  </p>
                  <div className="mt-1 space-y-1">
                    {event.attendees.map((attendee, idx) => (
                      <p key={idx} className="text-sm text-neutral-400">
                        {attendee}
                      </p>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="p-4 border-t border-neutral-800 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-neutral-400 hover:text-neutral-300 hover:bg-neutral-800 rounded-lg transition-colors"
          >
            Fechar
          </button>
          {event.html_link && (
            <a
              href={event.html_link}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-4 py-2 text-sm bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg transition-colors"
            >
              <ExternalLink className="w-4 h-4" />
              Abrir no Google Calendar
            </a>
          )}
        </div>
      </div>
    </div>
  )
}

export default EventDetailModal
